import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AddedByWaiterBadge, Badge } from '@/components/Badge';
import { buttonClasses } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Money } from '@/components/Money';
import type { Cart, CartLine } from './api';
import { useCustomer } from './context';
import {
  AddedByYouBadge,
  CartTotals,
  ListSkeleton,
  Notice,
  PageTitle,
  Panel,
  QuantityStepper,
  StickyActions,
} from './parts';
import { useCart, useRemoveCartItem, useUpdateCartItem } from './queries';
import { describeCartChange, describeCustomerError } from './wording';

const ISSUE_TEXT: Record<NonNullable<CartLine['issue']>, string> = {
  ITEM_UNAVAILABLE: 'No longer available. Remove it to continue.',
  INVALID_MODIFIERS: 'Its options have changed. Remove it and add it again.',
};

/** Read-only description of a cart line, shared by the cart and the review screen. */
export function CartLineSummary({ line }: { line: CartLine }) {
  return (
    <div className="min-w-0">
      <p className="text-base font-semibold text-ink">
        {line.name ?? 'Item'}{' '}
        <span className="font-medium text-muted tabular-nums">×{line.quantity}</span>
      </p>
      {line.modifiers.length > 0 && (
        <p className="text-sm text-muted">
          {line.modifiers.map((modifier) => modifier.name).join(', ')}
        </p>
      )}
      {line.notes && <p className="text-sm text-muted">Note: {line.notes}</p>}
      <p className="mt-1.5">
        {line.added_by === 'WAITER' ? <AddedByWaiterBadge /> : <AddedByYouBadge />}
      </p>
    </div>
  );
}

/**
 * The shared draft cart of the session. Re-read every few seconds while this screen is visible, because
 * a waiter may add to it and guests have no event stream.
 */
export function CartPage() {
  const cart = useCart({ poll: true });
  const { lastSeenCart } = useCustomer();
  const [change, setChange] = useState<string | null>(null);

  // Explain changes the customer did not make (a waiter's addition, an expired cart).
  const data = cart.data;
  useEffect(() => {
    if (!data) return;
    const previous = lastSeenCart.current;
    lastSeenCart.current = data;
    if (!previous) return;
    const words = describeCartChange(previous, data);
    if (words) setChange(words);
  }, [data, lastSeenCart]);

  // No "draft" badge here: the cart is the draft by definition; Orders marks it as one beside placed orders.
  const title = <PageTitle>Cart</PageTitle>;

  if (cart.isPending) {
    return (
      <>
        {title}
        <ListSkeleton rows={3} />
      </>
    );
  }
  if (cart.isError && !data) {
    return (
      <>
        {title}
        <ErrorState error={cart.error} onRetry={() => void cart.refetch()} />
      </>
    );
  }
  if (!data) return null;

  const changeNotice = change && (
    <div className="mb-4">
      <Notice
        tone="warn"
        action={
          <button
            type="button"
            className="min-h-11 px-2 font-semibold underline"
            onClick={() => setChange(null)}
          >
            OK
          </button>
        }
      >
        {change}
      </Notice>
    </div>
  );

  if (data.items.length === 0) {
    return (
      <>
        {title}
        {changeNotice}
        <EmptyState
          icon="cart"
          title="Your cart is empty"
          message="Items you or your waiter add will appear here."
          action={
            <Link to="/t/menu" className={buttonClasses({ variant: 'primary' })}>
              Browse the menu
            </Link>
          }
        />
      </>
    );
  }

  return (
    <>
      {title}
      {changeNotice}
      <CartLines cart={data} />
      <div className="mt-4">
        <CartTotals cart={data} />
      </div>
      {!data.is_valid && (
        <p id="cart-invalid" className="mt-3 text-sm font-medium text-attention-ink">
          Some items can't be ordered. Remove them to continue.
        </p>
      )}
      <Link
        to="/t/menu"
        className={buttonClasses({ variant: 'tertiary', fullWidth: true }) + ' mt-2 mb-4'}
      >
        Add more items
      </Link>
      <StickyActions>
        {data.is_valid ? (
          <Link
            to="/t/cart/review"
            className={buttonClasses({ variant: 'primary', size: 'lg', fullWidth: true })}
          >
            Review order
          </Link>
        ) : (
          <button
            type="button"
            disabled
            aria-describedby="cart-invalid"
            className={buttonClasses({ variant: 'primary', size: 'lg', fullWidth: true })}
          >
            Review order
          </button>
        )}
      </StickyActions>
    </>
  );
}

function CartLines({ cart }: { cart: Cart }) {
  const { money, session } = useCustomer();
  const update = useUpdateCartItem();
  const remove = useRemoveCartItem();
  const busyId = update.isPending
    ? update.variables.cartItemId
    : remove.isPending
      ? remove.variables
      : null;
  const failed = update.isError ? update.error : remove.isError ? remove.error : null;
  const failure = failed ? describeCustomerError(failed, session.locationType) : null;

  return (
    <>
      {failure && (
        <div className="mb-3">
          <Notice tone="error">
            <strong className="block">{failure.title}</strong>
            {failure.message}
          </Notice>
        </div>
      )}
      <Panel>
        <ul className="divide-y divide-line" aria-label="Items in your cart">
          {cart.items.map((line) => {
            const name = line.name ?? 'Item';
            const busy = busyId === line.cart_item_id;
            return (
              <li
                key={line.cart_item_id}
                className="flex flex-col gap-3 px-4 py-3"
                aria-busy={busy || undefined}
              >
                <div className="flex items-start justify-between gap-3">
                  <CartLineSummary line={line} />
                  {line.line_amount !== null ? (
                    <Money
                      value={money(line.line_amount)}
                      className="shrink-0 text-base font-bold text-ink"
                    />
                  ) : (
                    <Badge tone="neutral">Sold out</Badge>
                  )}
                </div>
                {line.issue && (
                  <p className="text-sm font-medium text-attention-ink">{ISSUE_TEXT[line.issue]}</p>
                )}
                <div className="flex items-center justify-between gap-3">
                  <QuantityStepper
                    value={line.quantity}
                    label={name}
                    disabled={busy}
                    onChange={(quantity) =>
                      update.mutate({ cartItemId: line.cart_item_id, quantity })
                    }
                  />
                  <button
                    type="button"
                    disabled={busy}
                    aria-label={`Remove ${name}`}
                    onClick={() => remove.mutate(line.cart_item_id)}
                    className={buttonClasses({ variant: 'tertiary' })}
                  >
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </Panel>
    </>
  );
}
